<template>
  <v-dialog v-model="dialog" scrollable lazy width="1024">
    <v-card>
      <v-toolbar dense color="primary" dark>
        <v-toolbar-title>
          {{
            rbpi._id
              ? $t('titles.global.updateModel', { field: $tc('models.rbpi') })
              : $t('titles.global.newAModel', { field: $tc('models.rbpi') })
          }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn dark icon @click="cancel">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-layout row wrap>
          <v-flex md6 xs12 px-2>
            <v-text-field
              v-model="rbpi.name"
              v-validate="'required'"
              autofocus
              :readonly="!edit"
              :label="$t('fields.global.name') + ' (*)'"
              :data-vv-name="$t('fields.global.name')"
              :error-messages="errors.collect($t('fields.global.name'))"
            />
          </v-flex>
          <v-flex md6 xs12 px-2>
            <v-text-field
              v-model="rbpi.description"
              :readonly="!edit"
              :label="$t('titles.roles.description')"
            />
          </v-flex>
          <v-flex md6 xs12 px-2>
            <v-autocomplete
              v-model="rbpi.stadiumId"
              v-validate="'required'"
              :readonly="!edit || !!stadiumId"
              :items="stadiumSelectList"
              :label="
                $t('action.actions.select', { field: $tc('models.stadium') }) +
                ' (*)'
              "
              :data-vv-name="$tc('models.stadium')"
              :error-messages="errors.collect($tc('models.stadium'))"
              :clearable="edit && !stadiumId"
              no-filter
              :loading="$store.state.stadiums.loading"
              :search-input.sync="searchStadium"
            >
              <template #selection="data">
                <v-chip
                  color="white"
                  :selected="data.selected"
                  class="chip--select-multi"
                >
                  <v-avatar>
                    <img :src="data.item.photo" />
                  </v-avatar>
                  {{ data.item.text }}
                </v-chip>
              </template>
              <template #item="data">
                <v-list-tile-avatar>
                  <img :src="data.item.photo" />
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title v-html="data.item.text" />
                </v-list-tile-content>
              </template>
            </v-autocomplete>
          </v-flex>
          <v-flex md6 xs12 px-2>
            <v-text-field
              v-model="rbpi.ip"
              v-validate="'required|ip'"
              :readonly="!edit"
              :label="$t('fields.global.ip') + ' (*)'"
              :data-vv-name="$t('fields.global.ip')"
              :error-messages="errors.collect($t('fields.global.ip'))"
              @change="onChangePath"
            />
            <!-- regex: /^([a-zA-Z0-9][a-zA-Z0-9-_]*\.)*[a-zA-Z0-9]*[a-zA-Z0-9-_]*[[a-zA-Z0-9]+$/ -->
          </v-flex>
          <v-flex md6 xs12 px-2>
            <v-text-field
              v-model="rbpi.hostname"
              v-validate="'required'"
              :readonly="!edit"
              :label="$t('fields.global.host') + ' (*)'"
              :data-vv-name="$t('fields.global.host')"
              :error-messages="errors.collect($t('fields.global.host'))"
            />
          </v-flex>
          <v-flex md6 xs12 px-2>
            <v-select
              v-model="rbpi.streamingBackend"
              :items="streamingBackends"
              :readonly="!edit"
              :label="$t('fields.global.streamingBackend')"
            />
          </v-flex>
          <v-flex md6 xs12 px-2>
            <v-select
              v-model="rbpi.videoRecoveryBackend"
              :items="videoRecoveryBackends"
              :readonly="!edit"
              :label="$t('fields.global.videoRecoveryBackend')"
            />
          </v-flex>
          <v-flex md6 xs12 px-2>
            <v-switch
              v-model="rbpi.active"
              :label="$t('fields.global.active')"
              color="primary"
              :readonly="!edit"
            />
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <template v-if="edit">
          <span style="color: red">(*) &nbsp;</span>
          <span>{{ $t('action.messages.fieldRequired') }}</span>
        </template>
        <v-spacer />
        <template v-if="edit">
          <meta-button type="reset" :disabled="loading" @click="clear" />
          <meta-button
            v-if="!rbpi._id"
            type="create"
            :loading="loading"
            @click="submit()"
          />
          <meta-button
            v-else
            type="update"
            :loading="loading"
            @click="submit()"
          />
        </template>
        <meta-button type="cancel" @click="cancel" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { parseObject } from '@/helpers/utils'

export default {
  name: 'RbpiForm',
  props: {
    dialog: {
      type: Boolean,
      default: false,
      required: true,
    },
    edit: {
      type: Boolean,
      default: true,
      required: false,
    },
    stadiumId: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      rbpi: {},
      searchStadium: '',
    }
  },
  watch: {
    dialog(val) {
      if (!val) this.cancel()
    },
    selected: {
      handler: function (val) {
        this.setObject(val)
      },
      immediate: true,
      deep: true,
    },
    stadiumId(val) {
      if (val) {
        this.rbpi.stadiumId = val
      }
    },
    searchStadium(val) {
      if (!this.rbpi.stadiumId) {
        this.debounceFetchSelect('stadiums', { match: val })
      }
    },
  },
  mounted() {
    this.setObject(this.selected)
  },
  computed: {
    ...mapState({
      selected: (state) => state.rbpi.selected,
      loading: (state) => state.rbpi.loading,
    }),
    ...mapGetters({
      stadiumSelectList: 'stadiums/selectList',
    }),
    streamingBackends() {
      return [
        {
          value: 'antmedia',
          text: this.$t('fields.global.streamingBackends.antmedia'),
        },
        {
          value: 'edge',
          text: this.$t('fields.global.streamingBackends.edge'),
        },
      ]
    },
    videoRecoveryBackends() {
      return [
        {
          value: 'http',
          text: this.$t('fields.global.videoRecoveryBackends.http'),
        },
        {
          value: 'edge',
          text: this.$t('fields.global.videoRecoveryBackends.edge'),
        },
      ]
    },
  },
  methods: {
    ...mapActions({
      setCreate: 'rbpi/create',
      setUpdate: 'rbpi/update',
      reset: 'rbpi/reset',
      fetchStadiums: 'stadiums/fetchSelectList',
    }),
    setObject(rbpi) {
      this.rbpi = parseObject(rbpi)
      if (this.rbpi.stadiumId) {
        this.fetchStadiums({ _id: this.rbpi.stadiumId })
      }
      if (this.stadiumId) this.rbpi.stadiumId = this.stadiumId
    },
    submit() {
      this.validate().then((valid) => {
        if (!valid) return
        let payload = Object.assign({}, this.rbpi)
        payload.path = `http://${payload.ip}`
        payload.apiKey = `TODO-REMOVE-THIS-PROPERY`
        if (!payload._id) {
          if (this.stadiumId) payload.stadiumId = this.stadiumId
          this.setCreate(payload).then((_) => {
            this.cancel()
          })
        } else {
          this.setUpdate(payload).then((_) => {
            this.cancel()
          })
        }
      })
    },
    onChangePath(path) {
      if (path) this.rbpi.path = path.trim()
    },
    cancel() {
      this.$emit('close')
      this.clear()
    },
    clear() {
      this.$nextTick((_) => {
        this.reset()
        this.$validator.reset()
      })
    },
  },
}
</script>
